import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { Button } from "@component/button";
import { defaultPagination } from "@constant/app";
import { cn } from "@util/tailwind";

interface Props {
  isLoading?: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  // Optional context-aware left summary:
  //  - total + pageSize → "1–10 of 42 employees" (current item range + total)
  //  - total only       → "42 employees total"
  //  - neither          → "Showing page X of Y"
  total?: number;
  pageSize?: number;
  itemLabel?: string;
  // Override container spacing/positioning (e.g. "mt-auto" to pin to the bottom
  // of a flex card). Merged via tailwind-merge so it wins over the defaults.
  className?: string;
  // Render the footer even on a single page (normally it returns null). Useful
  // when callers want a consistent footer/height across sibling cards whose
  // counts vary. Prev/next stay disabled since there's nowhere to page.
  alwaysShow?: boolean;
}

export function Pagination({
  isLoading = false,
  currentPage = defaultPagination.page,
  totalPages = 1,
  onPageChange,
  total,
  pageSize,
  itemLabel = "item",
  className,
  alwaysShow = false,
}: Props) {
  const handlePrevious = () => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  };

  // No items → keep the footer hidden even when alwaysShow is requested (avoids
  // a disabled "1–0 of 0" footer on an empty card).
  if (total === 0) {
    return null;
  }

  // Single page → nothing to paginate, unless the caller opts to keep the footer
  // for layout consistency (height parity across sibling cards).
  if (totalPages <= 1 && !alwaysShow) {
    return null;
  }

  const plural = (n: number) => `${itemLabel}${n === 1 ? "" : "s"}`;
  let summary: string;
  if (typeof total === "number" && typeof pageSize === "number" && pageSize > 0) {
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, total);
    summary = `${start}–${end} of ${total} ${plural(total)}`;
  } else if (typeof total === "number") {
    summary = `${total} ${plural(total)} total`;
  } else {
    summary = `Showing page ${currentPage} of ${totalPages}`;
  }

  return (
    <div
      className={cn(
        "flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4 mt-6",
        className,
      )}
    >
      <div className="text-sm text-gray-500 dark:text-gray-400">{summary}</div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrevious}
          disabled={currentPage <= 1 || isLoading}
          className="h-8 px-2"
          aria-label="Previous page"
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </Button>
        <span className="px-2 text-sm text-gray-600 dark:text-gray-400 tabular-nums">
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={handleNext}
          disabled={currentPage >= totalPages || isLoading}
          className="h-8 px-2"
          aria-label="Next page"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default Pagination;
